/**
 * Atmosphère visuelle course-only : dôme de ciel dégradé, disque solaire
 * (aligné sur la DirectionalLight de `setupTrackSceneAtmosphere`) et nuages
 * plats procéduraux. Pas utilisé hors vue Jouer / course.
 */
import * as THREE from 'three';
import { mulberry32 } from '@multirx/shared/game/prng.js';
import { trackSunLightPosition, type TrackSceneBounds } from './circuit3dTrackScene.js';

export interface RaceSkyCloudSpec {
  x: number;
  y: number;
  z: number;
  width: number;
  depth: number;
  rotY: number;
  opacity: number;
}

export interface RaceSkySunPosition {
  x: number;
  y: number;
  z: number;
}

export interface RaceSkyAtmosphereHandle {
  group: THREE.Group;
  dispose(): void;
}

/** Hash 32-bit stable (FNV-1a) pour grainer les nuages depuis un nom de circuit. */
export function hashRaceSkySeed(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Rayon du dôme ciel (même formule que le mesh). */
export function raceSkyDomeRadius(bounds: TrackSceneBounds): number {
  return Math.max(bounds.span * 7, 70);
}

/** Alias : même position que la DirectionalLight (`trackSunLightPosition`). */
export function raceSunLightOffset(bounds: TrackSceneBounds): RaceSkySunPosition {
  return trackSunLightPosition(bounds);
}

/**
 * Disque solaire sur le dôme, dans la direction lumière → visible en regardant
 * le tracé (pas derrière la caméra défaut).
 */
export function raceSunWorldPosition(bounds: TrackSceneBounds): RaceSkySunPosition {
  const { centerX, centerZ } = bounds;
  const light = trackSunLightPosition(bounds);
  const dx = light.x - centerX;
  const dy = light.y;
  const dz = light.z - centerZ;
  const len = Math.hypot(dx, dy, dz) || 1;
  const radius = raceSkyDomeRadius(bounds) * 0.88;
  return {
    x: centerX + (dx / len) * radius,
    y: (dy / len) * radius,
    z: centerZ + (dz / len) * radius,
  };
}

export function planRaceSkyClouds(
  bounds: TrackSceneBounds,
  seed: number,
  count?: number
): RaceSkyCloudSpec[] {
  const random = mulberry32(seed >>> 0);
  const { centerX, centerZ, span } = bounds;
  const n = count ?? Math.min(16, Math.max(6, 6 + Math.round(span / 4)));
  const clouds: RaceSkyCloudSpec[] = [];
  for (let i = 0; i < n; i++) {
    const angle = random() * Math.PI * 2;
    const radius = span * (0.35 + random() * 0.85);
    clouds.push({
      x: centerX + Math.cos(angle) * radius,
      y: span * (0.45 + random() * 0.7),
      z: centerZ + Math.sin(angle) * radius,
      width: span * (0.35 + random() * 0.55),
      depth: span * (0.18 + random() * 0.35),
      rotY: random() * Math.PI * 2,
      opacity: 0.45 + random() * 0.35,
    });
  }
  return clouds;
}

/**
 * Dôme ciel + disque solaire dans le même shader (fiable : pas de fog/depth/
 * frustum séparé qui fait disparaître un mesh soleil).
 */
function createSkyDomeMaterial(sunDirection: THREE.Vector3): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
    toneMapped: false,
    uniforms: {
      topColor: { value: new THREE.Color(0x4aa3dc) },
      bottomColor: { value: new THREE.Color(0xc8e7f7) },
      offset: { value: 0.12 },
      exponent: { value: 0.65 },
      sunDirection: { value: sunDirection.clone().normalize() },
      sunColor: { value: new THREE.Color(0xfff4b0) },
      glowColor: { value: new THREE.Color(0xffc857) },
    },
    vertexShader: /* glsl */ `
      varying vec3 vSkyDir;
      void main() {
        // Géométrie sphère centrée : position locale = direction ciel.
        vSkyDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform float offset;
      uniform float exponent;
      uniform vec3 sunDirection;
      uniform vec3 sunColor;
      uniform vec3 glowColor;
      varying vec3 vSkyDir;
      void main() {
        vec3 dir = normalize(vSkyDir);
        float h = dir.y;
        float t = max(pow(max(h + offset, 0.0), exponent), 0.0);
        vec3 col = mix(bottomColor, topColor, t);
        float mu = max(dot(dir, normalize(sunDirection)), 0.0);
        // Disque large + halo — doit se lire clairement dans la bande de ciel.
        float disc = smoothstep(0.98, 0.995, mu);
        float glow = pow(mu, 12.0) * 0.85;
        col += sunColor * disc * 4.0 + glowColor * glow;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
}

function createCloudTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    return tex;
  }
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const blobs: Array<{ x: number; y: number; r: number; a: number }> = [
    { x: 128, y: 64, r: 48, a: 0.55 },
    { x: 80, y: 70, r: 36, a: 0.45 },
    { x: 176, y: 58, r: 40, a: 0.5 },
    { x: 110, y: 48, r: 28, a: 0.4 },
    { x: 160, y: 78, r: 30, a: 0.38 },
  ];
  for (const blob of blobs) {
    const g = ctx.createRadialGradient(blob.x, blob.y, 0, blob.x, blob.y, blob.r);
    g.addColorStop(0, `rgba(255,255,255,${blob.a})`);
    g.addColorStop(0.55, `rgba(255,255,255,${blob.a * 0.45})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(blob.x, blob.y, blob.r, 0, Math.PI * 2);
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

export interface MountRaceSkyOptions {
  seed?: number;
  /** Ex. nom du circuit — hashé si `seed` absent. */
  seedKey?: string;
}

/**
 * Ajoute ciel / soleil / nuages plats à une scène déjà atmosphérée.
 * Pousse le brouillard un peu plus loin pour laisser les nuages lisibles.
 */
export function mountRaceSkyAtmosphere(
  scene: THREE.Scene,
  bounds: TrackSceneBounds,
  options?: MountRaceSkyOptions
): RaceSkyAtmosphereHandle {
  const seed =
    options?.seed ?? (options?.seedKey != null ? hashRaceSkySeed(options.seedKey) : 0xc1e1_0001);
  const { centerX, centerZ, span } = bounds;
  const group = new THREE.Group();
  group.name = 'race-sky-atmosphere';

  const skyRadius = raceSkyDomeRadius(bounds);
  const sunPos = raceSunWorldPosition(bounds);
  const sunDir = new THREE.Vector3(sunPos.x - centerX, sunPos.y, sunPos.z - centerZ).normalize();
  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(skyRadius, 64, 32),
    createSkyDomeMaterial(sunDir)
  );
  sky.position.set(centerX, 0, centerZ);
  sky.renderOrder = -10;
  sky.frustumCulled = false;
  group.add(sky);

  // Le dôme remplace le fond uni ; fog un peu plus long pour fondre le sol
  // dans l'horizon avant le bord géométrique (voir `trackDistantGroundSize`).
  scene.background = new THREE.Color(0x8fc7e8);
  if (scene.fog instanceof THREE.Fog) {
    scene.fog.near = span * 2.2;
    scene.fog.far = span * 7.5;
    scene.fog.color.set(0xa8d6ef);
  }

  const cloudTex = createCloudTexture();
  const cloudSpecs = planRaceSkyClouds(bounds, seed);
  for (const spec of cloudSpecs) {
    const mat = new THREE.MeshBasicMaterial({
      map: cloudTex,
      transparent: true,
      opacity: spec.opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const cloud = new THREE.Mesh(new THREE.PlaneGeometry(spec.width, spec.depth), mat);
    cloud.rotation.x = -Math.PI / 2;
    cloud.rotation.z = spec.rotY;
    cloud.position.set(spec.x, spec.y, spec.z);
    cloud.renderOrder = -5;
    group.add(cloud);
  }

  scene.add(group);

  return {
    group,
    dispose(): void {
      scene.remove(group);
      group.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        obj.geometry.dispose();
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of materials) {
          // Texture nuage partagée : disposée une seule fois après le traverse.
          if (m instanceof THREE.MeshBasicMaterial) m.map = null;
          m.dispose();
        }
      });
      cloudTex.dispose();
    },
  };
}

/** Far caméra conseillé pour englober le dôme (sinon clip du ciel). */
export function raceSkyCameraFar(bounds: TrackSceneBounds, currentFar = 500): number {
  return Math.max(currentFar, bounds.span * 12, 120);
}
